import React, { useEffect, useState } from 'react';
import { Container, Grid, Card, CardContent, Typography, Button, Stack, Box, CircularProgress, Avatar, Chip } from '@mui/material';
import { motion } from 'framer-motion';
import { Explore, BookmarkBorder, BarChart } from '@mui/icons-material';
import { httpGet, httpPost } from '../lib/http';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import studyPathsSeed from '../data/studyPathsSeed';

const UI_TOKENS = {
  heroGradient: 'linear-gradient(135deg, #0a7ea4 0%, #08607f 100%)',
  heroChipBg: 'rgba(255,255,255,0.15)',
  heroChipColor: '#fff',
  radius: '16px',
  primaryButton: {
    borderRadius: '16px',
    fontWeight: 700,
    textTransform: 'none',
  },
};

const MotionCard = motion(Card);

export default function StudyPathsList() {
  const [paths, setPaths] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(null);
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        const res = await httpGet('/api/study-paths');
        if (!mounted) return;
        if (!res.ok) {
          setPaths(studyPathsSeed);
          setLoading(false);
          return;
        }
        const data = await res.json();
        setPaths(Array.isArray(data) ? data : data.items || []);
      } catch (e) {
        setPaths(studyPathsSeed);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, []);

  async function handleStart(pathId) {
    if (!isAuthenticated) {
      navigate(`/login?from=${encodeURIComponent(`/study-paths/${pathId}`)}`);
      return;
    }
    setStarting(pathId);
    try {
      const userId = user?.id || user?.userId || user?._id;
      const res = await httpPost(`/api/users/${encodeURIComponent(userId)}/path-progress`, { pathId });
      navigate(`/study-paths/${pathId}`);
    } catch (e) {
      navigate(`/study-paths/${pathId}`);
    } finally {
      setStarting(null);
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Card
        sx={{
          mb: 4,
          borderRadius: UI_TOKENS.radius,
          color: '#fff',
          background: UI_TOKENS.heroGradient,
          boxShadow: '0 20px 40px rgba(10, 126, 164, 0.2)'
        }}
      >
        <CardContent sx={{ p: { xs: 3, md: 5 } }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
            <Box sx={{ maxWidth: 600 }}>
              <Typography variant="h4" fontWeight={900}>Your Roadmap to Success</Typography>
              <Typography variant="body1" sx={{ mt: 1.5, opacity: 0.9 }}>
                Choose a structured roadmap tailored for your target govt exam. Track progress, hit milestones, and master every topic with Questguru.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1.5}>
              <Box sx={{ px: 2, py: 1, borderRadius: UI_TOKENS.radius, bgcolor: UI_TOKENS.heroChipBg, border: '1px solid rgba(255,255,255,0.1)' }}>
                <Typography variant="caption" fontWeight={800} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Explore fontSize="inherit" /> Guided
                </Typography>
              </Box>
              <Box sx={{ px: 2, py: 1, borderRadius: UI_TOKENS.radius, bgcolor: UI_TOKENS.heroChipBg, border: '1px solid rgba(255,255,255,0.1)' }}>
                <Typography variant="caption" fontWeight={800} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <BarChart fontSize="inherit" /> Adaptive
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Grid container spacing={3} sx={{ mb: 6 }}>
        {[
          { icon: <Explore color="primary" />, title: "1. Pick Your Path", text: "Start with the exam stream that matches your target role." },
          { icon: <BookmarkBorder color="primary" />, title: "2. Follow Sequence", text: "Move subject-by-subject to keep your preparation focused." },
          { icon: <BarChart color="primary" />, title: "3. Track Mastery", text: "Monitor your progress and identify areas for revision." }
        ].map((item, idx) => (
          <Grid item xs={12} md={4} key={idx}>
            <Card sx={{ borderRadius: UI_TOKENS.radius, border: '1px solid', borderColor: 'divider', height: '100%', boxShadow: '0 4px 12px rgba(0,0,0,0.02)' }}>
              <CardContent>
                <Avatar sx={{ bgcolor: 'rgba(45, 91, 255, 0.08)', mb: 2 }}>{item.icon}</Avatar>
                <Typography variant="subtitle1" fontWeight={800}>{item.title}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{item.text}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={800}>Featured Study Paths</Typography>
        <Button variant="contained" onClick={() => navigate('/study-paths/select')} sx={UI_TOKENS.primaryButton}>Create Custom Path</Button>
      </Stack>

      <Grid container spacing={3}>
        {paths && paths.length ? paths.map((p, idx) => (
          <Grid item xs={12} md={6} lg={4} key={p.id}>
            <MotionCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              whileHover={{ translateY: -5 }}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: UI_TOKENS.radius,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 4px 20px rgba(0,0,0,0.04)'
              }}
            >
              <CardContent sx={{ flex: 1, p: 3 }}>
                <Stack spacing={2}>
                  <Typography variant="h6" fontWeight={800}>{p.title}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    {p.description}
                  </Typography>
                  <Chip
                    label={`Est. ${p.estimatedHours} hours`}
                    size="small"
                    variant="outlined"
                    sx={{ alignSelf: 'flex-start', fontWeight: 600, fontSize: '0.7rem' }}
                  />
                </Stack>
              </CardContent>
              <Box sx={{ p: 3, pt: 0 }}>
                <Stack direction="row" spacing={1.5}>
                  <Button fullWidth variant="outlined" sx={UI_TOKENS.primaryButton} onClick={() => navigate(`/study-paths/${p.id}`)}>View Roadmap</Button>
                  <Button
                    fullWidth
                    variant="contained"
                    sx={UI_TOKENS.primaryButton}
                    disabled={Boolean(starting) && starting !== p.id}
                    onClick={() => handleStart(p.id)}
                  >
                    {starting === p.id ? 'Starting…' : 'Start Prep'}
                  </Button>
                </Stack>
              </Box>
            </MotionCard>
          </Grid>
        )) : (
          <Grid item xs={12}>
            <Typography color="text.secondary">No study paths available.</Typography>
          </Grid>
        )}
      </Grid>
    </Container>
  );
}
